import React from 'react';
import { Row, Col, Button } from 'antd';
import { useDarkMode } from 'src/context/DarkModeContext';
import { QuizCategory, quizCategoryExplanation } from 'src/data/models/quiz/QuizCategoryModel';
import { QuizCategoryLabel } from 'src/utils/quizCategoryLabel';
import PageStructure from 'src/components/wrappers/PageStructure';

interface QuizCategorySelectionContentProps {
  selectedCategories: QuizCategory[];
  playQuiz: () => void;
  onCategorySelectionChange: (selectedCategories: QuizCategory[]) => void;
}

const QuizCategorySelectionContent: React.FC<QuizCategorySelectionContentProps> = ({
  selectedCategories,
  playQuiz,
  onCategorySelectionChange,
}) => {
  const { isDarkMode } = useDarkMode();

  const categories = Object.values(QuizCategory).filter((category) => category !== QuizCategory.ALL);

  const handleCategoryChange = (category: QuizCategory, checked: boolean) => {
    let newSelection: QuizCategory[] = [];

    if (category === QuizCategory.ALL) {
      onCategorySelectionChange(checked ? [QuizCategory.ALL, ...categories] : []);
      return;
    }

    const selectedCategoriesWithoutAll = selectedCategories.filter((c) => c !== QuizCategory.ALL);

    if (checked) {
      newSelection = [...selectedCategoriesWithoutAll, category];
    } else {
      newSelection = selectedCategoriesWithoutAll.filter((c) => c !== category);
    }

    if (newSelection.length === categories.length) {
      newSelection = [QuizCategory.ALL, ...categories];
    }

    onCategorySelectionChange(newSelection);
  };

  const isSelected = (category: QuizCategory) =>
    selectedCategories.includes(category) || selectedCategories.includes(QuizCategory.ALL);

  const getCardStyle = (selected: boolean): React.CSSProperties => ({
    border: `2px solid ${selected ? '#1890ff' : isDarkMode ? '#444' : '#ccc'}`,
    borderRadius: 12,
    padding: '12px 16px',
    backgroundColor: isDarkMode ? '#29292D' : '#fff',
    color: isDarkMode ? '#fff' : '#000',
    cursor: 'pointer',
    transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
    boxShadow: selected ? '0 0 8px rgba(24,144,255,0.4)' : 'none',
    display: 'flex',
    alignItems: 'center',
  });

  return (
    <div className={isDarkMode ? 'dark' : ''}>
      <div className="section">
        <p>{quizCategoryExplanation}</p>
      </div>

      <Row gutter={[16, 16]} className="section">
        {[...categories, QuizCategory.ALL].map((category) => {
          const selected = isSelected(category);
          return (
            <Col key={category} xs={24} sm={12} md={8} lg={6}>
              <div
                style={getCardStyle(selected)}
                onClick={() => handleCategoryChange(category, !selected)}
              >
                <input
                  type="checkbox"
                  checked={selected}
                  readOnly
                  style={{ marginRight: 8 }}
                />
                {QuizCategoryLabel[category] || 'Select All'}
              </div>
            </Col>
          );
        })}
      </Row>

      <div style={{ marginTop: 24 }}>
        <Button
          type="primary"
          onClick={playQuiz}
          block
          size="large"
          disabled={selectedCategories.length === 0}
        >
          Start Quiz
        </Button>
      </div>
    </div>
  );
};

const QuizCategorySelectionScreen: React.FC<QuizCategorySelectionContentProps> = (props) => {
  return (
    <PageStructure
      TitleNode="Select Quiz Categories"
      Page={<QuizCategorySelectionContent {...props} />}
    />
  );
};

export default QuizCategorySelectionScreen;
